import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Icon } from '../components/icons';
import { useApi } from '../hooks/useAuth';

interface AdminReport {
  id: string;
  reason: string;
  details: string | null;
  status: string;
  createdAt: string;
  reporter: { id: string; name: string; email: string };
  reported: { id: string; name: string; email: string; isActive: boolean; distinctReporters: number };
}

interface AdminUser {
  id: string; name: string; email: string; role: string; mode: string;
  isActive: boolean; postcode: string | null; reportsAgainst: number; createdAt: string;
}

const REASON_LABEL: Record<string, string> = {
  HARASSMENT: 'Harassment', INAPPROPRIATE: 'Inappropriate', FAKE_PROFILE: 'Fake profile',
  NO_SHOW: 'No-show', OTHER: 'Other',
};

type AdminView = 'reports' | 'users' | 'feedback' | 'safety' | 'audit' | 'funnel' | 'activity';

interface FunnelStage { key: string; label: string; count: number; proxy?: boolean }

interface ActivityEvent {
  id: string; type: string; data: any; createdAt: string;
  user: { id: string; name: string; email: string } | null;
}

const EVENT_LABEL: Record<string, string> = {
  onboarding_completed: 'onboarded', match_created: 'match', couple_formed: 'couple formed',
  date_proposed: 'date proposed', date_confirmed: 'date confirmed', date_declined: 'date declined',
  concierge_pull: 'concierge', report_filed: 'report', like_sent: 'like', couple_like: 'couple like',
};

// Concierge is the one event with a rich payload worth showing inline.
function eventSummary(e: { type: string; data: any }): string {
  const d = e.data ?? {};
  if (e.type === 'concierge_pull') return `“${(d.message ?? '').slice(0, 60)}” → ${(d.reply ?? '').slice(0, 60)}…`;
  if (e.type === 'match_created') return `${d.matchMode ?? ''} · spark ${d.sparkScore ?? '?'}`;
  if (e.type === 'date_proposed') return `${d.venueName ?? ''} ${d.date ?? ''} ${d.time ?? ''}`.trim();
  if (e.type === 'date_confirmed') return `${d.venueName ?? ''}`;
  if (e.type === 'report_filed') return `${d.reason ?? ''}${d.autoHidden ? ' · auto-hidden' : ''}`;
  return '';
}

interface AdminFeedback {
  id: string; createdAt: string; freeText: string | null;
  vibeRating: number | null; connectionRating: string | null; activityRating: number | null;
  groupChemistry: string | null; venueReturn: boolean | null;
  user: { id: string; name: string; email: string };
}

interface AdminCheckIn {
  id: string; checkInTime: string; respondedAt: string | null; alertSent: boolean;
  trustedContact: string; unresolved: boolean; user: { id: string; name: string; email: string };
}

interface AdminAudit {
  id: string; action: string; createdAt: string; actor: string; target: string | null; targetId: string | null;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function AdminPanel({ t, onClose }: { t: Theme; onClose: () => void }) {
  const { apiFetch } = useApi();
  const qc = useQueryClient();
  const [view, setView] = useState<AdminView>('reports');
  const [showAll, setShowAll] = useState(false);
  const [q, setQ] = useState('');
  const [dossierId, setDossierId] = useState<string | null>(null);
  const [funnelWindow, setFunnelWindow] = useState<'7' | '30' | 'all'>('30');
  const [activityType, setActivityType] = useState<string>('');

  const stats = useQuery({ queryKey: ['admin-stats'], queryFn: () => apiFetch('/api/admin/stats') });

  const reports = useQuery<{ reports: AdminReport[] }>({
    queryKey: ['admin-reports', showAll],
    queryFn: () => apiFetch(`/api/admin/reports${showAll ? '' : '?status=PENDING'}`),
    enabled: view === 'reports',
  });

  const users = useQuery<{ users: AdminUser[] }>({
    queryKey: ['admin-users', q],
    queryFn: () => apiFetch(`/api/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    enabled: view === 'users',
  });

  const feedback = useQuery<{ feedback: AdminFeedback[] }>({
    queryKey: ['admin-feedback'],
    queryFn: () => apiFetch('/api/admin/feedback'),
    enabled: view === 'feedback',
  });

  const safety = useQuery<{ checkIns: AdminCheckIn[] }>({
    queryKey: ['admin-safety'],
    queryFn: () => apiFetch('/api/admin/safety'),
    enabled: view === 'safety',
  });

  const audit = useQuery<{ actions: AdminAudit[] }>({
    queryKey: ['admin-audit'],
    queryFn: () => apiFetch('/api/admin/audit'),
    enabled: view === 'audit',
  });

  const funnel = useQuery<{ window: string; cohortSize: number; stages: FunnelStage[] }>({
    queryKey: ['admin-funnel', funnelWindow],
    queryFn: () => apiFetch(`/api/admin/funnel?window=${funnelWindow}`),
    enabled: view === 'funnel',
  });

  const activity = useQuery<{ events: ActivityEvent[] }>({
    queryKey: ['admin-activity', activityType],
    queryFn: () => apiFetch(`/api/admin/activity${activityType ? `?type=${activityType}` : ''}`),
    enabled: view === 'activity',
  });

  const reportAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      apiFetch(`/api/admin/reports/${id}/action`, { method: 'POST', body: JSON.stringify({ action }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-reports'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
    },
  });

  const userAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      apiFetch(`/api/admin/users/${id}/action`, { method: 'POST', body: JSON.stringify({ action }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
    },
  });

  const s = stats.data;

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 300, background: t.paper, display: 'flex', flexDirection: 'column' }}>
      {dossierId && <UserDossier t={t} userId={dossierId} onClose={() => setDossierId(null)} />}

      {/* Header */}
      <div style={{ flexShrink: 0, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${t.line}` }}>
        <button onClick={onClose} className="pm-press" style={{ all: 'unset', cursor: 'pointer', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: t.paperAlt }}>
          {Icon.back({ s: 18, c: t.ink })}
        </button>
        <h1 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 24, margin: 0, color: t.ink }}>
          Control panel
        </h1>
        <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 1.5, color: t.terracotta, textTransform: 'uppercase' }}>Admin</span>
      </div>

      {/* Stats strip */}
      <div style={{ flexShrink: 0, display: 'flex', gap: 8, padding: '12px 16px', overflowX: 'auto', borderBottom: `1px solid ${t.line}` }} className="scroll-container">
        {[
          { label: 'Users', value: s?.users, tint: t.ink },
          { label: 'Active', value: s?.activeUsers, tint: t.sage },
          { label: 'Suspended', value: s?.suspended, tint: t.inkMute },
          { label: 'Couples', value: s?.couples, tint: t.gold },
          { label: 'Matches', value: s?.matches, tint: t.terracotta },
          { label: 'Open reports', value: s?.pendingReports, tint: t.terracotta },
        ].map(st => (
          <div key={st.label} style={{ flexShrink: 0, minWidth: 82, padding: '10px 14px', borderRadius: 12, background: t.card, border: `1px solid ${t.line}` }}>
            <div style={{ fontFamily: FONTS.display, fontSize: 24, color: st.tint, lineHeight: 1 }}>{st.value ?? '—'}</div>
            <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: t.inkMute, marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{st.label}</div>
          </div>
        ))}
      </div>

      {/* View switch */}
      <div style={{ flexShrink: 0, display: 'flex', gap: 6, padding: '12px 16px 8px', overflowX: 'auto' }} className="scroll-container">
        {(['funnel', 'reports', 'users', 'activity', 'feedback', 'safety', 'audit'] as const).map(v => (
          <button key={v} onClick={() => setView(v)} style={{
            all: 'unset', cursor: 'pointer', padding: '7px 16px', borderRadius: 100, flexShrink: 0,
            fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
            background: view === v ? t.ink : t.paperAlt, color: view === v ? t.paper : t.inkMute,
          }}>
            {v}
          </button>
        ))}
        {view === 'reports' && (
          <button onClick={() => setShowAll(a => !a)} style={{
            all: 'unset', cursor: 'pointer', marginLeft: 'auto', padding: '7px 12px', borderRadius: 100, flexShrink: 0,
            fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, border: `1px solid ${t.line}`,
          }}>
            {showAll ? 'Pending only' : 'Show all'}
          </button>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 16px 24px' }} className="scroll-container">
        {view === 'funnel' ? (
          <FunnelView t={t} data={funnel.data} loading={funnel.isLoading} window={funnelWindow} onWindow={setFunnelWindow} />
        ) : view === 'reports' ? (
          <>
            {reports.isLoading && <Muted t={t}>Loading reports…</Muted>}
            {reports.data && reports.data.reports.length === 0 && <Muted t={t}>No {showAll ? '' : 'pending '}reports. 🎉</Muted>}
            {reports.data?.reports.map(r => (
              <div key={r.id} style={{ marginBottom: 10, borderRadius: 14, background: t.card, border: `1px solid ${r.reason === 'HARASSMENT' ? t.terracotta : t.line}`, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontFamily: FONTS.sans, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: r.reason === 'HARASSMENT' ? t.terracotta : t.paperAlt, color: r.reason === 'HARASSMENT' ? t.paper : t.inkSoft }}>
                    {REASON_LABEL[r.reason] ?? r.reason}
                  </span>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute }}>{new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                  <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, color: r.status === 'PENDING' ? t.terracotta : t.inkMute }}>{r.status}</span>
                </div>
                <button onClick={() => setDossierId(r.reported.id)} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.ink }}>
                    <strong>{r.reported.name}</strong> <span style={{ color: t.inkMute }}>&lt;{r.reported.email}&gt;</span>
                    {!r.reported.isActive && <span style={{ marginLeft: 6, fontSize: 11, color: t.terracotta, fontWeight: 600 }}>· hidden</span>}
                    <span style={{ marginLeft: 6, fontFamily: FONTS.mono, fontSize: 10, color: t.terracotta }}>→</span>
                  </div>
                </button>
                <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, marginTop: 2 }}>
                  reported by{' '}
                  <button onClick={() => setDossierId(r.reporter.id)} style={{ all: 'unset', cursor: 'pointer', color: t.inkSoft, textDecoration: 'underline' }}>
                    {r.reporter.name}
                  </button>
                  {' · '}{r.reported.distinctReporters} distinct reporter{r.reported.distinctReporters === 1 ? '' : 's'}
                </div>
                {r.details && (
                  <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 10, background: t.paperAlt, fontFamily: FONTS.sans, fontSize: 13, color: t.inkSoft, lineHeight: 1.5 }}>
                    “{r.details}”
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                  <ActionBtn t={t} label="Dismiss" onClick={() => reportAction.mutate({ id: r.id, action: 'dismiss' })} disabled={reportAction.isPending} />
                  {r.reported.isActive
                    ? <>
                        <ActionBtn t={t} label="Suspend" tone="warn" onClick={() => reportAction.mutate({ id: r.id, action: 'suspend' })} disabled={reportAction.isPending} />
                        <ActionBtn t={t} label="Ban" tone="danger" onClick={() => reportAction.mutate({ id: r.id, action: 'ban' })} disabled={reportAction.isPending} />
                      </>
                    : <ActionBtn t={t} label="Reactivate" tone="ok" onClick={() => reportAction.mutate({ id: r.id, action: 'reactivate' })} disabled={reportAction.isPending} />}
                </div>
              </div>
            ))}
          </>
        ) : view === 'users' ? (
          <>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search name or email…"
              style={{
                width: '100%', boxSizing: 'border-box', marginBottom: 12,
                border: `1.5px solid ${t.line}`, borderRadius: 12, padding: '11px 14px',
                fontFamily: FONTS.sans, fontSize: 14, color: t.ink, background: t.card, outline: 'none',
              }}
            />
            {users.isLoading && <Muted t={t}>Loading…</Muted>}
            {users.data?.users.map(u => (
              <div key={u.id} style={{ marginBottom: 8, borderRadius: 12, background: t.card, border: `1px solid ${t.line}`, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => setDossierId(u.id)} style={{ all: 'unset', cursor: 'pointer', minWidth: 0, flex: 1 }}>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.ink, fontWeight: 500 }}>
                    {u.name} {u.role !== 'CONSUMER' && <span style={{ fontSize: 10, color: t.terracotta, fontFamily: FONTS.mono }}>{u.role}</span>}
                    <span style={{ marginLeft: 5, fontFamily: FONTS.mono, fontSize: 10, color: t.terracotta }}>→</span>
                  </div>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.email} · {u.mode.toLowerCase()}{u.postcode ? ` · ${u.postcode}` : ''}
                    {u.reportsAgainst > 0 && <span style={{ color: t.terracotta }}> · {u.reportsAgainst} report{u.reportsAgainst === 1 ? '' : 's'}</span>}
                    {!u.isActive && <span style={{ color: t.terracotta, fontWeight: 600 }}> · hidden</span>}
                  </div>
                </button>
                {u.isActive
                  ? <ActionBtn t={t} label="Suspend" tone="warn" onClick={() => userAction.mutate({ id: u.id, action: 'suspend' })} disabled={userAction.isPending} />
                  : <ActionBtn t={t} label="Reactivate" tone="ok" onClick={() => userAction.mutate({ id: u.id, action: 'reactivate' })} disabled={userAction.isPending} />}
              </div>
            ))}
            {users.data && users.data.users.length === 0 && <Muted t={t}>No users found.</Muted>}
          </>
        ) : view === 'feedback' ? (
          <>
            {feedback.isLoading && <Muted t={t}>Loading feedback…</Muted>}
            {feedback.data && feedback.data.feedback.length === 0 && <Muted t={t}>No feedback submitted yet.</Muted>}
            {feedback.data?.feedback.map(f => (
              <div key={f.id} style={{ marginBottom: 8, borderRadius: 12, background: t.card, border: `1px solid ${t.line}`, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <button onClick={() => setDossierId(f.user.id)} style={{ all: 'unset', cursor: 'pointer', fontFamily: FONTS.sans, fontSize: 13, color: t.ink, fontWeight: 600 }}>
                    {f.user.name} <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.terracotta }}>→</span>
                  </button>
                  <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute }}>{fmtDate(f.createdAt)}</span>
                </div>
                {f.freeText && (
                  <div style={{ padding: '8px 12px', borderRadius: 10, background: t.paperAlt, fontFamily: FONTS.sans, fontSize: 13, color: t.inkSoft, lineHeight: 1.5, marginBottom: 8 }}>
                    “{f.freeText}”
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {f.vibeRating != null && <Tag t={t}>vibe {f.vibeRating}/5</Tag>}
                  {f.activityRating != null && <Tag t={t}>activity {f.activityRating}/5</Tag>}
                  {f.connectionRating && <Tag t={t} tone={f.connectionRating === 'YES' ? 'ok' : f.connectionRating === 'NO' ? 'bad' : undefined}>connection {f.connectionRating}</Tag>}
                  {f.groupChemistry && <Tag t={t}>chemistry {f.groupChemistry}</Tag>}
                  {f.venueReturn != null && <Tag t={t}>{f.venueReturn ? 'would return' : 'would not return'}</Tag>}
                </div>
              </div>
            ))}
          </>
        ) : view === 'safety' ? (
          <>
            {safety.isLoading && <Muted t={t}>Loading check-ins…</Muted>}
            {safety.data && safety.data.checkIns.length === 0 && <Muted t={t}>No safety check-ins recorded.</Muted>}
            {safety.data?.checkIns.map(c => {
              const bad = c.alertSent || c.unresolved;
              return (
                <div key={c.id} style={{ marginBottom: 8, borderRadius: 12, background: t.card, border: `1px solid ${bad ? t.terracotta : t.line}`, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => setDossierId(c.user.id)} style={{ all: 'unset', cursor: 'pointer', fontFamily: FONTS.sans, fontSize: 13.5, color: t.ink, fontWeight: 600 }}>
                      {c.user.name} <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.terracotta }}>→</span>
                    </button>
                    {c.alertSent && <Tag t={t} tone="bad">alert sent</Tag>}
                    {c.unresolved && !c.alertSent && <Tag t={t} tone="bad">no response</Tag>}
                    {c.respondedAt && <Tag t={t} tone="ok">responded</Tag>}
                  </div>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute, marginTop: 4 }}>
                    check-in {fmtDate(c.checkInTime)} · trusted contact {c.trustedContact}
                  </div>
                </div>
              );
            })}
          </>
        ) : view === 'activity' ? (
          <>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto' }} className="scroll-container">
              {['', 'concierge_pull', 'match_created', 'date_confirmed', 'couple_formed', 'report_filed'].map(ty => (
                <button key={ty || 'all'} onClick={() => setActivityType(ty)} style={{
                  all: 'unset', cursor: 'pointer', flexShrink: 0, padding: '5px 12px', borderRadius: 100,
                  fontFamily: FONTS.sans, fontSize: 11.5, fontWeight: 600,
                  background: activityType === ty ? t.ink : t.paperAlt, color: activityType === ty ? t.paper : t.inkMute,
                }}>
                  {ty ? (EVENT_LABEL[ty] ?? ty) : 'All'}
                </button>
              ))}
            </div>
            {activity.isLoading && <Muted t={t}>Loading activity…</Muted>}
            {activity.data && activity.data.events.length === 0 && <Muted t={t}>No activity yet — events appear here as users act.</Muted>}
            {activity.data?.events.map(e => {
              const summary = eventSummary(e);
              return (
                <div key={e.id} style={{ marginBottom: 6, borderRadius: 10, background: t.card, border: `1px solid ${t.line}`, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: FONTS.mono, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: e.type === 'concierge_pull' ? t.gold : e.type === 'report_filed' ? t.terracotta : t.paperAlt, color: (e.type === 'concierge_pull' || e.type === 'report_filed') ? t.paper : t.inkMute }}>
                      {EVENT_LABEL[e.type] ?? e.type}
                    </span>
                    {e.user && (
                      <button onClick={() => setDossierId(e.user!.id)} style={{ all: 'unset', cursor: 'pointer', fontFamily: FONTS.sans, fontSize: 13, color: t.ink, fontWeight: 500 }}>
                        {e.user.name} <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: t.terracotta }}>→</span>
                      </button>
                    )}
                    <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute }}>{fmtDate(e.createdAt)}</span>
                  </div>
                  {summary && (
                    <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft, marginTop: 5, lineHeight: 1.5, wordBreak: 'break-word' }}>
                      {summary}
                      {e.type === 'concierge_pull' && e.data?.latencyMs != null && (
                        <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute }}> · {e.data.latencyMs}ms · {e.data.outputTokens ?? '?'}tok</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <>
            {audit.isLoading && <Muted t={t}>Loading audit log…</Muted>}
            {audit.data && audit.data.actions.length === 0 && <Muted t={t}>No admin actions recorded yet.</Muted>}
            {audit.data?.actions.map(a => (
              <div key={a.id} style={{ marginBottom: 6, borderRadius: 10, background: t.card, border: `1px solid ${t.line}`, padding: '10px 14px' }}>
                <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.ink }}>
                  <strong>{a.actor}</strong> <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: t.terracotta }}>{a.action}</span>
                  {a.target && (
                    <> → <button onClick={() => a.targetId && setDossierId(a.targetId)} style={{ all: 'unset', cursor: a.targetId ? 'pointer' : 'default', textDecoration: 'underline' }}>{a.target}</button></>
                  )}
                </div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute, marginTop: 3 }}>{fmtDate(a.createdAt)}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function Muted({ t, children }: { t: Theme; children: React.ReactNode }) {
  return <div style={{ textAlign: 'center', padding: '40px 20px', fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute }}>{children}</div>;
}

// Derived cohort funnel — bars are % of the top stage (clamped, since directly-
// created legacy matches can make an intermediate stage exceed the previous one).
// A step losing >40% of the prior stage is flagged as a cliff.
function FunnelView({ t, data, loading, window, onWindow }: {
  t: Theme;
  data?: { cohortSize: number; stages: FunnelStage[] };
  loading: boolean;
  window: '7' | '30' | 'all';
  onWindow: (w: '7' | '30' | 'all') => void;
}) {
  const top = data?.stages?.[0]?.count ?? 0;
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {(['7', '30', 'all'] as const).map(w => (
          <button key={w} onClick={() => onWindow(w)} style={{
            all: 'unset', cursor: 'pointer', padding: '6px 14px', borderRadius: 100,
            fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600,
            background: window === w ? t.terracotta : t.paperAlt, color: window === w ? t.paper : t.inkMute,
          }}>
            {w === 'all' ? 'All time' : `${w}d`}
          </button>
        ))}
      </div>

      {loading && <Muted t={t}>Crunching…</Muted>}
      {data && top === 0 && <Muted t={t}>No signups in this window.</Muted>}

      {data && top > 0 && data.stages.map((s, i) => {
        const prev = i === 0 ? s.count : data.stages[i - 1].count;
        const pctTop = Math.round((s.count / top) * 100);
        const step = prev > 0 ? Math.round((s.count / prev) * 100) : 0;
        const cliff = i > 0 && step < 60 && step <= 100; // big real drop (ignore >100% artifacts)
        return (
          <div key={s.key} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.ink, fontWeight: 500 }}>
                {s.label}
              </span>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                {i > 0 && (
                  <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: cliff ? t.terracotta : t.inkMute, fontWeight: cliff ? 700 : 400 }}>
                    {step > 100 ? '—' : `${step}%`}
                  </span>
                )}
                <span style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink }}>{s.count}</span>
              </span>
            </div>
            <div style={{ height: 12, borderRadius: 100, background: t.paperAlt, overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(100, pctTop)}%`, height: '100%', borderRadius: 100,
                background: cliff ? t.terracotta : s.proxy ? t.gold : t.sage,
                transition: 'width 400ms cubic-bezier(0.22,1,0.36,1)',
              }} />
            </div>
          </div>
        );
      })}

      {data && top > 0 && (
        <div style={{ marginTop: 18, fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute, lineHeight: 1.6 }}>
          Each bar is % of the top stage; the small number is conversion from the step above. Cliffs (losing &gt;40%) are in terracotta.
          <br />* “Date happened” is a proxy — users who left post-date feedback — until the post-date check-in ships.
        </div>
      )}
    </div>
  );
}

function Tag({ t, children, tone }: { t: Theme; children: React.ReactNode; tone?: 'ok' | 'bad' }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 100,
      fontFamily: FONTS.sans, fontSize: 10.5, fontWeight: 600,
      background: tone === 'bad' ? t.terracotta : tone === 'ok' ? t.sage : t.paperAlt,
      color: tone ? t.paper : t.inkMute,
      border: tone ? 'none' : `1px solid ${t.line}`,
    }}>{children}</span>
  );
}

function Section({ t, title, count, children }: { t: Theme; title: string; count?: number; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.5, color: t.inkMute, fontWeight: 600, marginBottom: 8 }}>
        {title}{count != null && ` · ${count}`}
      </div>
      {children}
    </div>
  );
}

// Full dossier for one user — everything we hold on them in one scroll.
function UserDossier({ t, userId, onClose }: { t: Theme; userId: string; onClose: () => void }) {
  const { apiFetch } = useApi();
  const d = useQuery<any>({ queryKey: ['admin-dossier', userId], queryFn: () => apiFetch(`/api/admin/users/${userId}`) });
  const u = d.data?.user;

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 320, background: t.paper, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexShrink: 0, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${t.line}` }}>
        <button onClick={onClose} className="pm-press" style={{ all: 'unset', cursor: 'pointer', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: t.paperAlt }}>
          {Icon.back({ s: 18, c: t.ink })}
        </button>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink }}>{u?.name ?? 'Loading…'}</div>
          {u && <div style={{ fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 16px 32px' }} className="scroll-container">
        {d.isLoading && <Muted t={t}>Loading dossier…</Muted>}
        {u && (
          <>
            <Section t={t} title="Account">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <Tag t={t} tone={u.isActive ? 'ok' : 'bad'}>{u.isActive ? 'active' : 'hidden'}</Tag>
                <Tag t={t}>{u.mode?.toLowerCase()}</Tag>
                {u.role !== 'CONSUMER' && <Tag t={t} tone="bad">{u.role}</Tag>}
                {u.age && <Tag t={t}>{u.age}y</Tag>}
                {u.gender && <Tag t={t}>{u.gender}</Tag>}
                {u.postcode && <Tag t={t}>{u.postcode}</Tag>}
                <Tag t={t}>{u.photoCount} photo{u.photoCount === 1 ? '' : 's'}</Tag>
                {!u.onboardingCompleted && <Tag t={t} tone="bad">not onboarded</Tag>}
                {u.isVerified && <Tag t={t} tone="ok">verified</Tag>}
              </div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute, marginTop: 8 }}>
                joined {fmtDate(u.createdAt)}{u.lastActiveAt ? ` · last active ${fmtDate(u.lastActiveAt)}` : ''}
              </div>
              {u.bio && <div style={{ marginTop: 8, fontFamily: FONTS.sans, fontSize: 13, color: t.inkSoft, lineHeight: 1.5 }}>“{u.bio}”</div>}
              {!!u.activities?.length && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                  {u.activities.map((a: string) => <Tag key={a} t={t}>{a}</Tag>)}
                </div>
              )}
            </Section>

            {d.data.couple && (
              <Section t={t} title="Couple">
                <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.ink }}>
                  with <strong>{d.data.couple.partner}</strong> · {d.data.couple.status}
                </div>
              </Section>
            )}

            <Section t={t} title="Reports against them" count={d.data.reportsAgainst.length}>
              {d.data.reportsAgainst.length === 0 ? <Muted t={t}>None.</Muted> : d.data.reportsAgainst.map((r: any) => (
                <div key={r.id} style={{ marginBottom: 6, padding: '10px 12px', borderRadius: 10, background: t.card, border: `1px solid ${r.reason === 'HARASSMENT' ? t.terracotta : t.line}` }}>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.ink }}>
                    {REASON_LABEL[r.reason] ?? r.reason} — by <strong>{r.by.name}</strong>
                  </div>
                  {r.details && <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft, marginTop: 4 }}>“{r.details}”</div>}
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute, marginTop: 4 }}>{fmtDate(r.createdAt)} · {r.status}</div>
                </div>
              ))}
            </Section>

            <Section t={t} title="Reports they filed" count={d.data.reportsBy.length}>
              {d.data.reportsBy.length === 0 ? <Muted t={t}>None.</Muted> : d.data.reportsBy.map((r: any) => (
                <div key={r.id} style={{ marginBottom: 6, padding: '10px 12px', borderRadius: 10, background: t.card, border: `1px solid ${t.line}` }}>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.ink }}>
                    {REASON_LABEL[r.reason] ?? r.reason} — against <strong>{r.against.name}</strong>
                  </div>
                  {r.details && <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft, marginTop: 4 }}>“{r.details}”</div>}
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute, marginTop: 4 }}>{fmtDate(r.createdAt)} · {r.status}</div>
                </div>
              ))}
            </Section>

            <Section t={t} title="Feedback they submitted" count={d.data.feedback.length}>
              {d.data.feedback.length === 0 ? <Muted t={t}>None.</Muted> : d.data.feedback.map((f: any) => (
                <div key={f.id} style={{ marginBottom: 6, padding: '10px 12px', borderRadius: 10, background: t.card, border: `1px solid ${t.line}` }}>
                  {f.freeText && <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkSoft, marginBottom: 6 }}>“{f.freeText}”</div>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {f.vibeRating != null && <Tag t={t}>vibe {f.vibeRating}/5</Tag>}
                    {f.activityRating != null && <Tag t={t}>activity {f.activityRating}/5</Tag>}
                    {f.connectionRating && <Tag t={t}>connection {f.connectionRating}</Tag>}
                    {f.groupChemistry && <Tag t={t}>chemistry {f.groupChemistry}</Tag>}
                  </div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute, marginTop: 5 }}>{fmtDate(f.createdAt)}</div>
                </div>
              ))}
            </Section>

            <Section t={t} title="Matches" count={d.data.matches.length}>
              {d.data.matches.length === 0 ? <Muted t={t}>None.</Muted> : d.data.matches.map((m: any) => (
                <div key={m.id} style={{ marginBottom: 5, padding: '9px 12px', borderRadius: 10, background: t.card, border: `1px solid ${t.line}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.ink, flex: 1 }}>{m.other}</span>
                  <Tag t={t}>{m.mode}</Tag>
                  <Tag t={t}>spark {m.sparkScore}</Tag>
                  <Tag t={t} tone={m.status === 'CANCELLED' ? 'bad' : undefined}>{m.status}</Tag>
                </div>
              ))}
            </Section>

            {d.data.bookings.length > 0 && (
              <Section t={t} title="Bookings" count={d.data.bookings.length}>
                {d.data.bookings.map((b: any) => (
                  <div key={b.id} style={{ marginBottom: 5, padding: '9px 12px', borderRadius: 10, background: t.card, border: `1px solid ${t.line}`, fontFamily: FONTS.sans, fontSize: 12.5, color: t.ink }}>
                    {b.venueName ?? 'Venue'} · {fmtDate(b.bookingDate)} · party {b.partySize} · {b.status}
                  </div>
                ))}
              </Section>
            )}

            {(d.data.blocking.length > 0 || d.data.blockedBy.length > 0) && (
              <Section t={t} title="Blocks">
                <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkSoft, lineHeight: 1.6 }}>
                  {d.data.blocking.length > 0 && <div>Blocking: {d.data.blocking.join(', ')}</div>}
                  {d.data.blockedBy.length > 0 && <div>Blocked by: {d.data.blockedBy.join(', ')}</div>}
                </div>
              </Section>
            )}

            {d.data.adminLog.length > 0 && (
              <Section t={t} title="Admin actions on this user" count={d.data.adminLog.length}>
                {d.data.adminLog.map((a: any, i: number) => (
                  <div key={i} style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, marginBottom: 3 }}>
                    <span style={{ fontFamily: FONTS.mono, color: t.terracotta }}>{a.action}</span> by {a.actor} · {fmtDate(a.createdAt)}
                  </div>
                ))}
              </Section>
            )}

            <Section t={t} title="Activity timeline" count={d.data.activity?.length ?? 0}>
              {(!d.data.activity || d.data.activity.length === 0) ? <Muted t={t}>No recorded activity.</Muted> : d.data.activity.map((e: any) => {
                const summary = eventSummary(e);
                return (
                  <div key={e.id} style={{ marginBottom: 5, paddingLeft: 10, borderLeft: `2px solid ${t.line}` }}>
                    <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.ink }}>
                      <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: e.type === 'concierge_pull' ? t.gold : t.terracotta }}>{EVENT_LABEL[e.type] ?? e.type}</span>
                      {summary && <span style={{ color: t.inkSoft }}> — {summary}</span>}
                    </div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 9.5, color: t.inkMute }}>{fmtDate(e.createdAt)}</div>
                  </div>
                );
              })}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function ActionBtn({ t, label, onClick, disabled, tone }: { t: Theme; label: string; onClick: () => void; disabled?: boolean; tone?: 'warn' | 'danger' | 'ok' }) {
  const bg = tone === 'danger' ? t.terracotta : tone === 'ok' ? t.sage : tone === 'warn' ? t.gold : t.paperAlt;
  const fg = tone ? t.paper : t.inkSoft;
  return (
    <button onClick={onClick} disabled={disabled} className="pm-press-soft" style={{
      all: 'unset', cursor: disabled ? 'default' : 'pointer',
      padding: '7px 14px', borderRadius: 10, textAlign: 'center',
      background: bg, color: fg, opacity: disabled ? 0.5 : 1,
      border: tone ? 'none' : `1px solid ${t.line}`,
      fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 600,
    }}>{label}</button>
  );
}
